import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiClock, FiMapPin, FiCamera } = FiIcons;

const BirdingDashboard = () => {
  const recentSightings = [
    { name: 'Serendib Scops Owl', count: 2, rarity: 'Rare', color: 'text-red-400' },
    { name: 'Red-faced Malkoha', count: 5, rarity: 'Elusive', color: 'text-orange-400' },
    { name: 'Sri Lanka Blue Magpie', count: 'Daily', rarity: 'Common', color: 'text-blue-400' },
    { name: 'White-faced Starling', count: 8, rarity: 'Common', color: 'text-green-400' },
  ];

  const tours = [
    {
      name: 'Kudawe Bird Wave Tour',
      time: 'Morning (5:00 AM)',
      duration: '4 hours',
      highlight: 'Mixed-species flocks',
      icon: FiTrendingUp
    },
    {
      name: 'Ullinduwawa Border Transect',
      time: 'Evening (3:00 PM)',
      duration: '3 hours',
      highlight: 'Skulker species',
      icon: FiMapPin
    },
    {
      name: 'Night Walk Experience',
      time: 'Night (7:00 PM)',
      duration: '2 hours',
      highlight: 'Owls & Frogmouths',
      icon: FiClock
    }
  ];

  return (
    <section id="birding" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
            Live Birding Dashboard
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Real-time sightings from the Kudawe-Ullinduwawa ornithological nexus
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Recent Sightings */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="glass rounded-2xl p-8"
          >
            <div className="flex items-center mb-6">
              <SafeIcon icon={FiCamera} className="text-emerald-400 text-2xl mr-3" />
              <h3 className="text-2xl font-bold text-white">Recent Sightings</h3>
            </div>
            <div className="space-y-4">
              {recentSightings.map((bird, index) => (
                <motion.div
                  key={bird.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex justify-between items-center p-4 glass-dark rounded-lg hover-lift"
                >
                  <div>
                    <h4 className="text-white font-semibold">{bird.name}</h4>
                    <span className={`text-sm ${bird.color}`}>{bird.rarity}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold text-lg">{bird.count}</div>
                    <div className="text-gray-400 text-sm">This week</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Tour Options */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="glass rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold text-white mb-6">Specialized Tours</h3>
            <div className="space-y-6">
              {tours.map((tour, index) => (
                <motion.div
                  key={tour.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-dark rounded-lg p-6 hover-lift cursor-pointer group"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-3 rounded-full">
                      <SafeIcon icon={tour.icon} className="text-white text-xl" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold text-lg mb-2 group-hover:text-emerald-300 transition-colors">
                        {tour.name}
                      </h4>
                      <div className="space-y-1">
                        <p className="text-gray-300">{tour.time}</p>
                        <p className="text-gray-400 text-sm">Duration: {tour.duration}</p>
                        <p className="text-emerald-400 text-sm font-medium">{tour.highlight}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="glass rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-4 font-playfair">
              The Proximity of a Lodge, The Comfort of a Resort
            </h3>
            <p className="text-gray-300 mb-6 text-lg">
              Experience the Sinharaja Bird Wave from your air-conditioned suite, 
              just 1.1km from the legendary Kudawe entrance.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover-lift"
            >
              Reserve Your Birding Experience
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BirdingDashboard;